import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { REGIONS, SupportedRegion, type Region } from '@ditto/shared';
import { type FC } from 'react';
import { CA, EU, GB, US } from 'country-flag-icons/string/3x2';
import flagWorld from '@/assets/flag-world.svg';

const toDataUrl = (svg: string) =>
  `data:image/svg+xml,${encodeURIComponent(svg)}`;

export interface RegionSelectorProps {
  value: SupportedRegion;
  onValueChange: (value: SupportedRegion) => void;
  disabled?: boolean;
}

const REGION_DATA: Record<Region, { flagImage: string; name: string }> = {
  [REGIONS.US]: {
    flagImage: toDataUrl(US),
    name: 'United States',
  },
  [REGIONS.EU]: {
    flagImage: toDataUrl(EU),
    name: 'European Union',
  },
  [REGIONS.UK]: {
    flagImage: toDataUrl(GB),
    name: 'United Kingdom',
  },
  [REGIONS.CA]: {
    flagImage: toDataUrl(CA),
    name: 'Canada',
  },
  [REGIONS.WORLD]: {
    flagImage: flagWorld,
    name: 'Global',
  },
};

export const RegionSelector: FC<RegionSelectorProps> = ({
  value,
  onValueChange,
  disabled,
}) => {
  const selectedDisplay = REGION_DATA[value];

  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger
        className={cn(
          'w-fit border-0 shadow-none p-2 h-fit space-x-2 ring-0 no-underline focus:ring-0 bg-neutral-100',
          'hover:bg-secondary',
          'data-[state=open]:bg-secondary data-[state=open]:text-neutral-700 hover:data-[placeholder]:text-neutral-700'
        )}
      >
        {selectedDisplay ? (
          <div className="flex items-center gap-[6px]">
            <img
              src={selectedDisplay.flagImage}
              alt=""
              className="w-[16px] h-[16px] object-contain"
            />
            <span className="text-[12px] font-medium">
              {selectedDisplay.name}
            </span>
          </div>
        ) : (
          <SelectValue placeholder="Select region" />
        )}
      </SelectTrigger>
      <SelectContent>
        {(Object.keys(REGION_DATA) as Region[]).map(region => {
          const data = REGION_DATA[region];
          const disabled = region === REGIONS.UK || region === REGIONS.CA;
          const comingSoon = disabled;
          return (
            <SelectItem key={region} value={region} disabled={disabled}>
              <div className="flex items-center gap-[6px]">
                <img
                  src={data.flagImage}
                  alt=""
                  className="w-[16px] h-[16px] object-contain"
                />
                <div className="flex flex-col">
                  <span className="font-medium text-[12px]">{data.name}</span>
                  {comingSoon && (
                    <span className="text-[11px] text-muted-foreground">
                      Coming soon
                    </span>
                  )}
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};
